/**
 * AppHeader Component
 * Application header with user info, sync status, and action buttons
 */

import React from 'react';
import { ArrowUpRight, Check, ChevronDown, Download, Moon, Sun, Search, LogOut, Route, User as UserIcon } from 'lucide-react';
import { cn, type ThemeClasses } from '@/ui/theme/ThemeContext';
import type { SelfServicePlanTier } from '@/shared/apiTypes';
import type { User } from '@/features/auth/services/authService';
import { SyncStatus } from '@/ui/components/feedback/SyncStatus';
import { MentionInboxPopover, type InboxNotification } from './MentionInboxPopover';
import { useSettings } from '@/features/settings/contexts/SettingsContext';
import type { AdminViewAsUserOption } from '@/features/auth/services/authService';
import { APP_Z_INDEX } from '@/ui/theme/layers';
import { usePresenceTransition } from '@/shared/hooks/usePresenceTransition';
import { AccountPlanPopover } from './AccountPlanPopover';
import { SELF_SERVICE_GUIDED_TOUR_DIALOG_ID } from './SelfServiceGuidedTour';

const ACCOUNT_PLAN_POPOVER_ID = 'account-plan-popover';

interface AppHeaderProps {
  theme: ThemeClasses;
  user: User | null;
  actualUser?: User | null;
  viewAsUserId?: string | null;
  viewAsOptions?: AdminViewAsUserOption[];
  isViewingAs?: boolean;
  activeViewLabel: string;
  workspaceLabel: string;
  workspaceDetail: string;
  hasUnsavedChanges: boolean;
  isDarkMode: boolean;
  hasPermission: (resource: string, action: string) => boolean;
  feedbackControls?: React.ReactNode;
  /**
   * True inside SAI Projects. SAI already owns profile, search, appearance,
   * sign-out and billing around the frame, so the header omits them there.
   */
  isSaiEmbedded?: boolean;
  canOpenSelfServiceTour?: boolean;
  onOpenSelfServiceTour?: () => void;
  onOpenPricing?: () => void;
  onOpenImport?: () => void;
  toggleDarkMode: () => void;
  setShowSearch: (show: boolean) => void;
  onOpenNotificationTarget: (notification: InboxNotification) => Promise<void> | void;
  setViewAsUserId?: (userId: string | null) => Promise<void>;
  updateSelfServicePlan?: (plan: SelfServicePlanTier) => Promise<void>;
  logout: () => Promise<void>;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
  theme,
  user,
  actualUser,
  viewAsUserId,
  viewAsOptions = [],
  activeViewLabel,
  workspaceLabel,
  workspaceDetail,
  hasUnsavedChanges,
  isDarkMode,
  hasPermission,
  feedbackControls,
  isSaiEmbedded = false,
  canOpenSelfServiceTour = false,
  onOpenSelfServiceTour,
  onOpenPricing,
  onOpenImport,
  toggleDarkMode,
  setShowSearch,
  onOpenNotificationTarget,
  setViewAsUserId,
  updateSelfServicePlan,
  logout,
}) => {
  const { language, setLanguage, t } = useSettings();
  const [isViewAsOpen, setIsViewAsOpen] = React.useState(false);
  const [isAccountPlanOpen, setIsAccountPlanOpen] = React.useState(false);
  const [viewAsSearch, setViewAsSearch] = React.useState('');
  const { shouldRender: shouldRenderViewAsMenu, isActive: isViewAsMenuActive } = usePresenceTransition(isViewAsOpen, { duration: 160 });
  const { shouldRender: shouldRenderAccountPlan, isActive: isAccountPlanActive } = usePresenceTransition(isAccountPlanOpen, { duration: 160 });
  const viewAsMenuRef = React.useRef<HTMLDivElement | null>(null);
  const accountPlanTriggerRef = React.useRef<HTMLButtonElement | null>(null);
  const canSwitchViewAs = actualUser?.godMode === true && Boolean(setViewAsUserId);
  const isSelfServiceOwner = Boolean(user?.permissions?.['self_service.owner'] || user?.selfServicePlan);
  const canOpenAccountPlan = isSelfServiceOwner && !isSaiEmbedded;
  const userChipCanOpenAccountPlan = canOpenAccountPlan && !canSwitchViewAs;
  const isUserChipInteractive = canSwitchViewAs || userChipCanOpenAccountPlan;
  const canOpenPricing = Boolean(onOpenPricing);
  const canAccessUpgrade = hasPermission('upgrade', 'access');
  const shouldShowUpgradeCta = !isSaiEmbedded && Boolean(user) && canAccessUpgrade && (canOpenAccountPlan || canOpenPricing);
  // Embedded, the chip remains only as the god-mode view-as switch so an active
  // impersonation is always visible and reversible inside SAI.
  const shouldShowAccountChip = !isSaiEmbedded || canSwitchViewAs;
  // Embedded controls use a quieter 32px / 16px scale; standalone sizes are unchanged.
  const iconButtonSize = isSaiEmbedded ? 'h-8 w-8' : 'h-10 w-10';
  const iconSize = isSaiEmbedded ? 16 : 20;
  const iconStrokeWidth = isSaiEmbedded ? 1.75 : 2;
  const controlHeight = isSaiEmbedded ? 'h-8' : 'h-9';
  // Softer, round controls inside SAI Projects to match SAI's own toolbar; standalone keeps its square corners.
  const controlShape = isSaiEmbedded ? 'rounded-full' : 'rounded-md';
  const selectedViewAsValue = viewAsUserId || '';
  const displayRole = isSelfServiceOwner ? t('Owner') : user?.role ? t(user.role) : '';
  const planLabel = user?.selfServicePlan?.name ? t(user.selfServicePlan.name) : '';
  const displayUserLabel = user
    ? isSelfServiceOwner
      ? [displayRole, planLabel].filter(Boolean).join(' · ')
      : `${user.username}${displayRole ? ` · ${displayRole}` : ''}`
    : t('Unknown user');
  const visibleUserLabel = displayUserLabel.length > 24
    ? `${displayUserLabel.slice(0, 21)}...`
    : displayUserLabel;
  const viewAsMenuOptions = React.useMemo(() => {
    if (!actualUser) return [];
    return [
      {
        id: '',
        username: actualUser.username,
        role: actualUser.role,
        email: actualUser.email,
      },
      ...viewAsOptions
        .filter((option) => option.id !== actualUser.id)
        .map((option) => ({
          id: option.id,
          username: option.username,
          role: option.role,
          email: option.email,
        })),
    ];
  }, [actualUser, viewAsOptions]);
  const filteredViewAsMenuOptions = React.useMemo(() => {
    const normalizedSearch = viewAsSearch.trim().toLowerCase();
    if (!normalizedSearch) return viewAsMenuOptions;
    return viewAsMenuOptions.filter((option) => {
      const haystack = [
        option.username,
        option.email,
        t(option.role),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [t, viewAsMenuOptions, viewAsSearch]);

  React.useEffect(() => {
    if (!isViewAsOpen) {
      setViewAsSearch('');
    }
  }, [isViewAsOpen]);

  React.useEffect(() => {
    if (!isViewAsOpen && !isAccountPlanOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      const isInsideAccountPlanTrigger = accountPlanTriggerRef.current?.contains(target) ?? false;
      if (!viewAsMenuRef.current?.contains(target) && !isInsideAccountPlanTrigger) {
        setIsViewAsOpen(false);
        setIsAccountPlanOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const shouldRestoreAccountPlanFocus = isAccountPlanOpen && canOpenAccountPlan;
        setIsViewAsOpen(false);
        setIsAccountPlanOpen(false);
        if (shouldRestoreAccountPlanFocus) {
          window.setTimeout(() => {
            accountPlanTriggerRef.current?.focus({ preventScroll: true });
          }, 0);
        }
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [canOpenAccountPlan, isAccountPlanOpen, isViewAsOpen]);

  React.useEffect(() => {
    const handleOpenAccountPlan = () => {
      if (!canOpenAccountPlan) {
        return;
      }
      setIsViewAsOpen(false);
      setIsAccountPlanOpen(true);
    };

    window.addEventListener('app:open-account-plan', handleOpenAccountPlan);
    return () => {
      window.removeEventListener('app:open-account-plan', handleOpenAccountPlan);
    };
  }, [canOpenAccountPlan]);

  const handleLogout = async () => {
    if (
      hasUnsavedChanges &&
      !window.confirm(t('You have unsaved changes. Are you sure you want to logout?'))
    ) {
      return;
    }
    await logout();
  };

  const handleUpgradeCta = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (canOpenAccountPlan) {
      accountPlanTriggerRef.current = event.currentTarget;
      setIsViewAsOpen(false);
      setIsAccountPlanOpen((current) => !current);
      return;
    }

    setIsViewAsOpen(false);
    setIsAccountPlanOpen(false);
    onOpenPricing?.();
  };

  return (
    <header
      className={cn(
        'pm-app-header relative flex min-w-0 flex-wrap items-center justify-between gap-2 overflow-visible border-b px-3 py-2 sm:px-4 lg:flex-nowrap lg:gap-3 lg:px-6',
        theme.bgPrimary,
        theme.borderPrimary
      )}
    >
      <div className="min-w-0 flex-1 basis-[12rem]">
        <div className={cn('text-[10px] font-semibold uppercase leading-none tracking-[0.18em]', theme.textMuted)}>
          {activeViewLabel}
        </div>
        <div className="mt-0.5 flex min-w-0 items-center gap-2">
          <h1 className={cn('truncate text-[1.05rem] font-semibold leading-tight sm:text-lg', theme.textPrimary)}>
            {workspaceLabel}
          </h1>
          <span className={cn('hidden max-w-[18rem] truncate text-xs lg:inline', theme.textSecondary)}>
            {workspaceDetail}
          </span>
        </div>
      </div>

      <div className="pm-app-header-actions ml-auto flex min-w-0 max-w-full shrink items-center justify-end gap-1 overflow-visible">
        {feedbackControls}

        {/* Sync Status */}
        <SyncStatus />

        {canOpenSelfServiceTour ? (
          <button
            type="button"
            data-testid="self-service-tour-trigger"
            data-tour-target="tour-trigger"
            aria-label={t('Tour')}
            aria-haspopup="dialog"
            aria-controls={SELF_SERVICE_GUIDED_TOUR_DIALOG_ID}
            title={t('Tour')}
            onClick={onOpenSelfServiceTour}
            className={cn(
              `inline-flex ${controlHeight} shrink-0 items-center justify-center gap-1.5 ${controlShape} px-2 text-sm font-medium transition-colors`,
              isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-zinc-100',
              theme.textSecondary,
            )}
          >
            <Route size={16} strokeWidth={iconStrokeWidth} className={theme.textMuted} aria-hidden="true" />
            <span className="hidden sm:inline">{t('Tour')}</span>
          </button>
        ) : null}

        {shouldShowUpgradeCta ? (
          <button
            ref={canOpenAccountPlan ? accountPlanTriggerRef : undefined}
            type="button"
            data-testid="app-upgrade-trigger"
            aria-label={canOpenAccountPlan ? t('Upgrade plan') : t('View pricing plans')}
            aria-haspopup={canOpenAccountPlan ? 'dialog' : undefined}
            aria-expanded={canOpenAccountPlan ? isAccountPlanOpen : undefined}
            aria-controls={canOpenAccountPlan ? ACCOUNT_PLAN_POPOVER_ID : undefined}
            onClick={handleUpgradeCta}
            className="inline-flex h-9 min-w-[2.25rem] shrink-0 items-center justify-center gap-1.5 rounded-md bg-[linear-gradient(135deg,#1d4ed8,#0e7490_55%,#047857)] px-2.5 text-sm font-semibold text-white shadow-[0_10px_24px_rgba(37,99,235,0.22)] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(6,182,212,0.28)] focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-cyan-300/60 dark:focus:ring-offset-zinc-950 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
            title={canOpenAccountPlan ? t('Upgrade plan') : t('View pricing plans')}
          >
            <ArrowUpRight size={15} aria-hidden="true" />
            <span className="hidden sm:inline">{t('Upgrade')}</span>
          </button>
        ) : null}

        {shouldShowAccountChip && (
          <>
          <div className={cn('w-px h-6', theme.borderPrimary)} />

          {/* User Info */}
          <div
            ref={viewAsMenuRef}
            className={cn(
              'flex w-10 shrink-0 xl:w-auto',
              canOpenAccountPlan ? 'static sm:relative' : 'relative',
            )}
          >
            {isUserChipInteractive ? (
              <button
                ref={userChipCanOpenAccountPlan ? accountPlanTriggerRef : undefined}
                type="button"
                data-testid={canSwitchViewAs ? 'admin-view-as-trigger' : 'current-user-chip'}
                data-tour-target={userChipCanOpenAccountPlan ? 'account-plan' : undefined}
                data-admin-view-as-value={selectedViewAsValue}
                aria-haspopup={canSwitchViewAs ? 'menu' : 'dialog'}
                aria-expanded={canSwitchViewAs ? isViewAsOpen : isAccountPlanOpen}
                aria-controls={userChipCanOpenAccountPlan ? ACCOUNT_PLAN_POPOVER_ID : undefined}
                aria-label={canSwitchViewAs ? t('View as user') : t('Account and Plan')}
                onClick={(event) => {
                  if (canSwitchViewAs) {
                    setIsAccountPlanOpen(false);
                    setIsViewAsOpen((current) => !current);
                  } else {
                    accountPlanTriggerRef.current = event.currentTarget;
                    setIsViewAsOpen(false);
                    setIsAccountPlanOpen((current) => !current);
                  }
                }}
                className={cn(
                  `flex ${controlHeight} w-10 max-w-none cursor-pointer items-center gap-2 overflow-hidden ${controlShape} px-2 text-sm transition-colors xl:w-auto xl:max-w-[14rem] xl:px-3`,
                  isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-zinc-100',
                  theme.textSecondary,
                )}
                title={canSwitchViewAs ? t('View as user') : isSelfServiceOwner && user ? `${user.username} · ${displayUserLabel}` : displayUserLabel}
              >
                <UserIcon size={16} strokeWidth={iconStrokeWidth} className={cn('shrink-0', theme.textMuted)} />
                <span className="hidden min-w-0 whitespace-nowrap xl:inline">
                  {visibleUserLabel}
                </span>
                <ChevronDown size={14} className={cn('hidden shrink-0 xl:block', theme.textMuted)} />
              </button>
            ) : (
              // A chip that opens nothing is plain text, not a focusable button that does nothing.
              <div
                data-testid="current-user-chip"
                data-admin-view-as-value={selectedViewAsValue}
                className={cn(
                  'flex h-9 w-10 max-w-none cursor-default items-center gap-2 overflow-hidden rounded-md px-2 text-sm xl:w-auto xl:max-w-[14rem] xl:px-3',
                  theme.textSecondary,
                )}
                title={displayUserLabel}
              >
                <UserIcon size={16} className={cn('shrink-0', theme.textMuted)} aria-hidden="true" />
                <span className="hidden min-w-0 whitespace-nowrap xl:inline">
                  {visibleUserLabel}
                </span>
                <span className="sr-only xl:hidden">{displayUserLabel}</span>
              </div>
            )}

            {canSwitchViewAs && shouldRenderViewAsMenu && (
              <div
                data-testid="admin-view-as-menu"
                data-zoom-safe-dropdown="true"
                className={cn(
                  'absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-1rem)] origin-top-right overflow-hidden rounded-md border shadow-lg transition-[opacity,transform] duration-150 ease-out motion-reduce:transition-none',
                  isViewAsMenuActive ? 'translate-y-0 scale-100 opacity-100' : '-translate-y-1 scale-[0.985] opacity-0',
                  theme.bgPrimary,
                  theme.borderPrimary,
                )}
                style={{ zIndex: APP_Z_INDEX.headerDropdown }}
              >
                <div className={cn('border-b px-2 py-2', theme.borderPrimary)}>
                  <label className="relative block">
                    <Search size={14} className={cn('pointer-events-none absolute left-2 top-1/2 -translate-y-1/2', theme.textMuted)} />
                    <input
                      type="search"
                      aria-label={t('Search view-as users')}
                      placeholder={t('Search users')}
                      value={viewAsSearch}
                      onChange={(event) => setViewAsSearch(event.target.value)}
                      className={cn(
                        'h-8 w-full rounded-md border pl-7 pr-2 text-xs outline-none transition-colors',
                        theme.bgSecondary,
                        theme.borderPrimary,
                        theme.textPrimary,
                      )}
                    />
                  </label>
                </div>
                {/* The search box sits outside the menu: a menu may only own menu items. */}
                <div className="max-h-64 overflow-y-auto py-1" role="menu" aria-label={t('View as user')}>
                {filteredViewAsMenuOptions.length ? filteredViewAsMenuOptions.map((option) => {
                  const optionValue = option.id;
                  const isSelected = selectedViewAsValue === optionValue;
                  const optionRole = t(option.role);
                  return (
                    <button
                      key={optionValue || 'self'}
                      type="button"
                      role="menuitemradio"
                      aria-checked={isSelected}
                      data-testid="admin-view-as-option"
                      data-user-id={optionValue}
                      onClick={() => {
                        void setViewAsUserId?.(optionValue || null);
                        setIsViewAsOpen(false);
                      }}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors',
                        isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-zinc-100',
                        isSelected ? theme.textPrimary : theme.textSecondary,
                      )}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">
                          {option.username} ({optionRole})
                        </span>
                        {option.email && (
                          <span className={cn('block truncate text-xs', theme.textMuted)}>
                            {option.email}
                          </span>
                        )}
                      </span>
                      {isSelected && <Check size={15} className={theme.textMuted} />}
                    </button>
                  );
                }) : (
                  <div className={cn('px-3 py-3 text-xs', theme.textMuted)}>
                    {t('No matching users')}
                  </div>
                )}
                </div>
              </div>
            )}

            {canOpenAccountPlan && shouldRenderAccountPlan && (
              <AccountPlanPopover
                id={ACCOUNT_PLAN_POPOVER_ID}
                theme={theme}
                plan={user?.selfServicePlan}
                isActive={isAccountPlanActive}
                onChangePlan={updateSelfServicePlan}
              />
            )}
          </div>
          </>
        )}

        <div className={cn('w-px h-6', theme.borderPrimary)} />

        <MentionInboxPopover
          theme={theme}
          isDarkMode={isDarkMode}
          compact={isSaiEmbedded}
          onOpenNotificationTarget={onOpenNotificationTarget}
        />

        <button
          type="button"
          aria-label={t('Switch language')}
          onClick={() => { void setLanguage(language === 'en' ? 'ko' : 'en'); }}
          className={cn(
            isSaiEmbedded
              ? 'inline-flex h-8 min-w-[2rem] items-center justify-center rounded-full px-2 text-xs font-medium tracking-wide transition-colors'
              : 'inline-flex h-9 min-w-[2.25rem] items-center justify-center rounded-md px-2 text-[13px] font-semibold tracking-wide transition-colors',
            theme.textSecondary,
            isDarkMode ? 'hover:bg-gray-800' : 'hover:bg-zinc-100'
          )}
          title={t('Switch language')}
        >
          {language === 'en' ? 'EN' : 'KO'}
        </button>

        {/* TeamGantt import (the header lost its only entry point in April; the modal lives in App) */}
        {onOpenImport && hasPermission('system', 'import') && (
          <button
            type="button"
            data-testid="app-import-trigger"
            aria-label={t('Import TeamGantt CSV')}
            onClick={onOpenImport}
            className={cn(
              `inline-flex ${iconButtonSize} items-center justify-center ${controlShape} border border-transparent transition-colors`,
              isDarkMode
                ? 'bg-transparent hover:bg-gray-800'
                : 'bg-transparent hover:bg-zinc-100',
              theme.textSecondary
            )}
            title={t('Import TeamGantt CSV')}
          >
            <Download size={iconSize} strokeWidth={iconStrokeWidth} />
          </button>
        )}

        {/* Search Button (SAI owns search around the embedded frame) */}
        {!isSaiEmbedded && hasPermission('system', 'search') && (
          <button
            id="app-search-trigger"
            type="button"
            aria-label={t('Search (⌘K)')}
            onClick={() => setShowSearch(true)}
            className={cn(
              'inline-flex h-10 w-10 items-center justify-center rounded-md border border-transparent transition-colors',
              isDarkMode
                ? 'bg-transparent hover:bg-gray-800'
                : 'bg-transparent hover:bg-zinc-100',
              theme.textSecondary
            )}
            title={t('Search (⌘K)')}
          >
            <Search size={20} />
          </button>
        )}

        {/* Appearance and sign-out belong to SAI while embedded; SAI drives appearance. */}
        {!isSaiEmbedded && (
          <>
            {/* Dark Mode Toggle */}
            <button
              type="button"
              data-testid="app-theme-toggle"
              aria-label={t('Switch to {{mode}} mode', { mode: t(isDarkMode ? 'light' : 'dark') })}
              onClick={toggleDarkMode}
              className={cn(
                'inline-flex h-10 w-10 items-center justify-center rounded-md border border-transparent transition-colors',
                  isDarkMode
                    ? 'bg-transparent hover:bg-gray-800'
                    : 'bg-transparent hover:bg-zinc-100',
                  theme.textSecondary
                )}
              title={t('Switch to {{mode}} mode', { mode: t(isDarkMode ? 'light' : 'dark') })}
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <div className={cn('w-px h-6', theme.borderPrimary)} />

            {/* Logout Button */}
            <button
              type="button"
              aria-label={t('Logout')}
              onClick={handleLogout}
              className={cn(
                'inline-flex h-10 w-10 items-center justify-center rounded-md border border-transparent transition-colors',
                isDarkMode
                  ? 'bg-transparent hover:bg-gray-800'
                  : 'bg-transparent hover:bg-zinc-100',
                'text-red-500 hover:text-red-600'
              )}
              title={t('Logout')}
            >
              <LogOut size={20} />
            </button>
          </>
        )}
      </div>
    </header>
  );
};
